import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RUN_TIMEOUT_MS } from "../lib/pythonRunner";
import { AlertIcon, CheckIcon, ChevronRightIcon, ClockIcon, EyeOffIcon, XIcon } from "./Icons";

function Summary({ result, onNext, onReview, hasNext, nextLabel }) {
  const { t } = useTranslation();
  const total = result.tests.length;
  const failed = result.tests.filter((test) => test.status !== "passed").length;

  if (result.status === "passed") {
    return (
      <div className="summary summary--success" role="status">
        <CheckIcon size={18} strokeWidth={2.5} />
        <div className="summary__text">
          <strong>{t("results.passed", { count: total })}</strong>
          {onReview && <span>{t("results.solvedBody")}</span>}
        </div>
        <div className="summary__actions">
          {onReview && (
            <button type="button" className="button button--ghost button--small" onClick={onReview}>
              {t("level.tabs.review")}
            </button>
          )}
          {hasNext && (
            <button type="button" className="button button--primary button--small" onClick={onNext}>
              {nextLabel || t("level.next")}
              <ChevronRightIcon />
            </button>
          )}
        </div>
      </div>
    );
  }
  if (result.status === "timeout") {
    return (
      <div className="summary summary--warning" role="alert">
        <ClockIcon size={18} />
        <div className="summary__text">
          <strong>{t("results.timeout", { seconds: Math.round(RUN_TIMEOUT_MS / 1000) })}</strong>
          <span>{t("results.timeoutHelp")}</span>
        </div>
      </div>
    );
  }
  if (result.status === "error") {
    return (
      <div className="summary summary--danger" role="alert">
        <AlertIcon size={18} />
        <div className="summary__text">
          <strong>{t("results.error")}</strong>
        </div>
      </div>
    );
  }
  return (
    <div className="summary summary--danger" role="alert">
      <XIcon size={18} strokeWidth={2.5} />
      <div className="summary__text">
        <strong>{t("results.failed", { failed, total })}</strong>
      </div>
    </div>
  );
}

function TestResults({ result, running, onNext, onReview, hasNext, nextLabel }) {
  const { t } = useTranslation();
  const [view, setView] = useState("tests");
  const output = result ? [result.stdout, result.stderr].filter(Boolean).join("\n") : "";

  return (
    <section className="results" aria-live="polite" aria-busy={running}>
      <div className="results__tabs" role="tablist">
        {["tests", "output"].map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={view === name}
            className={view === name ? "is-active" : ""}
            onClick={() => setView(name)}
          >
            {t(`results.${name}`)}
            {name === "tests" && result?.tests.length > 0 && (
              <span className="count">
                {result.tests.filter((test) => test.status === "passed").length}/{result.tests.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="results__body">
        {!result && !running && <p className="muted results__empty">{t("results.empty")}</p>}
        {running && <div className="results__running" aria-label={t("editor.running")}><span className="spinner" /> {t("editor.running")}</div>}

        {result && !running && view === "tests" && (
          <>
            <Summary result={result} onNext={onNext} onReview={onReview} hasNext={hasNext} nextLabel={nextLabel} />
            {result.error && <pre className="traceback">{result.error}</pre>}
            {result.tests.length > 0 && (
              <ul className="test-list">
                {result.tests.map((test, index) => {
                  const passed = test.status === "passed";
                  return (
                    <li key={`${test.name}-${index}`} className={`test ${passed ? "test--passed" : "test--failed"}`}>
                      <div className="test__row">
                        <span className="test__icon">{passed ? <CheckIcon size={14} strokeWidth={3} /> : <XIcon size={14} strokeWidth={3} />}</span>
                        <span className="test__name">{test.name}</span>
                        {test.hidden && (
                          <span className="test__hidden">
                            <EyeOffIcon size={12} /> {t("results.hidden")}
                          </span>
                        )}
                        {typeof test.durationMs === "number" && (
                          <span className="test__duration">{t("results.duration", { ms: Math.round(test.durationMs) })}</span>
                        )}
                      </div>
                      {!passed && test.message && <pre className="test__message">{test.message}</pre>}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {result && !running && view === "output" && (
          output ? <pre className="stdout">{output}</pre> : <p className="muted results__empty">{t("results.noOutput")}</p>
        )}
      </div>
    </section>
  );
}

export default TestResults;
